/* eslint-disable react/no-danger */
import React, { useState } from "react";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// ANT DESIGN COMPONENTS
import { Button, Tooltip } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import FormInputText from "../../../../../components/form/FormInputText";
import FormInputNumber from "../../../../../components/form/FormInputNumber";
import FormSelectOpcDataType from "../../../../../components/form/FormSelectOpcDataType";
// INTERFACES
import {
  ReadItemResponseInterface,
  DataRepresentationInterface,
  DataInterface,
  ReadGroupResponseInterface,
} from "../../../../../../interfaces/AdapterCdoOpc.interface";

export default function ComponentReadGroupResponse({
  data,
  setData,
}: {
  data: ReadGroupResponseInterface;
  setData: (newValue: ReadGroupResponseInterface) => void;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [response, setResponse] = useState<ReadItemResponseInterface>({
    data: { u16: 1 },
    item_name: "",
    group_name: "",
    quality: 0,
  });

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onChangeValue = (newValue: string | number, variableName: string) => {
    const copy = structuredClone(response);
    if (variableName === "item_name" && typeof newValue === "string") {
      copy.item_name = newValue;
    } else if (variableName === "group_name" && typeof newValue === "string") {
      copy.group_name = newValue;
    } else if (variableName === "quality" && typeof newValue === "number") {
      copy.quality = newValue;
    }
    setResponse(copy);
  };

  const parseDataValue = (data_: DataInterface) => {
    const [, dataValue] = Object.entries(data_)[0];
    return dataValue;
  };

  const parseDataKey = (data_: DataInterface): DataRepresentationInterface => {
    const [dataKey]: any = Object.entries(data_)[0];
    return dataKey;
  };

  const setDataType = (newValue: DataRepresentationInterface) => {
    const copy = structuredClone(response);
    if (newValue === "u16") {
      copy.data = { u16: 1 };
    } else if (newValue === "i16") {
      copy.data = { i16: 1 };
    } else if (newValue === "u32") {
      copy.data = { u32: 1 };
    } else if (newValue === "i32") {
      copy.data = { i32: 1 };
    } else if (newValue === "float") {
      copy.data = { float: 1 };
    } else if (newValue === "u64") {
      copy.data = { u64: 1 };
    } else if (newValue === "i64") {
      copy.data = { i64: 1 };
    } else if (newValue === "double") {
      copy.data = { double: 1 };
    } else if (newValue === "string") {
      copy.data = { string: "" };
    }
    setResponse(copy);
  };

  const onChangeDataValueNumber = (newValue: number) => {
    const copy = structuredClone(response);
    if (parseDataKey(response.data) === "u16") {
      copy.data = { u16: newValue };
    } else if (parseDataKey(response.data) === "i16") {
      copy.data = { i16: newValue };
    } else if (parseDataKey(response.data) === "u32") {
      copy.data = { u32: newValue };
    } else if (parseDataKey(response.data) === "i32") {
      copy.data = { i32: newValue };
    } else if (parseDataKey(response.data) === "float") {
      copy.data = { float: newValue };
    } else if (parseDataKey(response.data) === "u64") {
      copy.data = { u64: newValue };
    } else if (parseDataKey(response.data) === "i64") {
      copy.data = { i64: newValue };
    } else if (parseDataKey(response.data) === "double") {
      copy.data = { double: newValue };
    }
    setResponse(copy);
  };

  const onChangeDataValueString = (newValue: string) => {
    const copy = structuredClone(response);
    copy.data = { string: newValue };
    setResponse(copy);
  };

  const onClickAddNewValue = () => {
    const copy = structuredClone(data);
    copy.responses.push(response);
    setData(copy);
    setResponse({
      data: { u16: 1 },
      item_name: "",
      group_name: "",
      quality: 0,
    });
  };

  const formatJsonWithColors = (obj: any) => {
    const json = JSON.stringify(obj, null, 4)
      .replace(/"([^"]+)"\s*:/g, '<span style="color:#9cdcfe;">"$1"</span>:')
      .replace(/: "([^"]*)"/g, ': <span style="color:#ce9178;">"$1"</span>')
      .replace(/:\s*(\d+)/g, ': <span style="color:#b5cea8;">$1</span>');
    return json;
  };

  const onClickRemoveItem = (index: number) => {
    const copy = structuredClone(data);
    copy.responses.splice(index, 1);
    setData(copy);
  };

  // ************************************************ */
  // USE EFFECT ************************************* */

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <FormSelectOpcDataType
        inputValue={parseDataKey(response.data)}
        inputOnChange={setDataType}
        disabledInput={false}
      />
      {parseDataKey(response.data) !== "string" && (
        <FormInputNumber
          inputValue={parseDataValue(response.data)}
          inputLabelName={t("general.dataValue")}
          inputOnChange={onChangeDataValueNumber}
          inputVariableName=""
        />
      )}
      {parseDataKey(response.data) === "string" && (
        <FormInputText
          inputValue={parseDataValue(response.data)}
          inputLabelName={t("general.dataValue")}
          inputOnChange={onChangeDataValueString}
          inputVariableName=""
          isRequired
          disabledInput={false}
        />
      )}
      <FormInputText
        inputValue={response.item_name}
        inputLabelName={t("general.itemName")}
        inputOnChange={onChangeValue}
        inputVariableName="item_name"
        isRequired
        disabledInput={false}
      />
      <FormInputText
        inputValue={response.group_name}
        inputLabelName={t("general.groupName")}
        inputOnChange={onChangeValue}
        inputVariableName="group_name"
        isRequired
        disabledInput={false}
      />
      <FormInputNumber
        inputValue={response.quality}
        inputLabelName={t("general.quality")}
        inputOnChange={onChangeValue}
        inputVariableName="quality"
      />
      <div className="col-md-12 mt-3">
        <div className="generalStyles__flex">
          <div className="generalStyles__inputFlexNameLarge">
            <span className="generalStyles__inputFlexRequired">*</span>
            <span className="drawer__text">{t("general.responses")}</span>
          </div>
          <div className="generalStyles__width100">
            {data.responses.map((item: ReadItemResponseInterface, itemIndex: number) => (
              <div
                key={itemIndex}
                className="mb-3"
                style={{
                  backgroundColor: "#1f1f1f",
                  borderRadius: "5px",
                  fontSize: "9px",
                  padding: "10px",
                }}
              >
                <div
                  style={{
                    width: "100%",
                  }}
                >
                  <pre
                    style={{
                      width: "100%",
                      color: "#dcdcdc",
                      padding: "10px",
                      borderRadius: "8px",
                      overflowX: "auto",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formatJsonWithColors(item),
                    }}
                  />
                  <div className="generalStyles__flexCenter">
                    <Tooltip title={t("general.delete")}>
                      <Button
                        onClick={() => {
                          onClickRemoveItem(itemIndex);
                        }}
                        className="buttonStyle__29"
                        shape="circle"
                        icon={<FontAwesomeIcon icon={faTrash} />}
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="generalStyles__infoTooltipIconBlank" />
        </div>
      </div>
      <div className="col-md-12 mt-3">
        <div className="generalStyles__flex">
          <div className="generalStyles__inputFlexNameLarge">
            <span className="generalStyles__inputFlexRequired" />
          </div>
          <div className="generalStyles__width100">
            <Button
              onClick={onClickAddNewValue}
              className="buttonStyle__28 generalStyles__width100"
            >
              <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
              {t("general.add")}
            </Button>
          </div>
          <div className="generalStyles__infoTooltipIconBlank" />
        </div>
      </div>
    </div>
  );
}
